import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { getArticleById, articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">文章不存在</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const otherHot = articles
    .filter((a) => a.id !== article.id && a.categorySlug !== article.categorySlug && a.isHot)
    .slice(0, 3);

  return (
    <>
      <JsonLd data={{
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Article",
            "headline": article.title,
            "description": article.excerpt,
            "image": article.image,
            "datePublished": article.date,
            "author": { "@type": "Organization", "name": "黑料吃瓜在线" },
            "publisher": { "@type": "Organization", "name": "黑料吃瓜在线" },
            "url": `http://yusuwangluo.cn/article/${article.id}`,
            "mainEntityOfPage": { "@type": "WebPage", "@id": `http://yusuwangluo.cn/article/${article.id}` }
          },
          {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": "http://yusuwangluo.cn/" },
              { "@type": "ListItem", "position": 2, "name": article.category, "item": `http://yusuwangluo.cn/category/${article.categorySlug}` },
              { "@type": "ListItem", "position": 3, "name": article.title }
            ]
          }
        ]
      }} />
      <Navbar />
      <main>
        <article className="pt-20 md:pt-24 pb-16 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: article.category, path: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />

            <header className="mt-6 mb-10">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-accent-500 text-background-50">
                  {article.category}
                </span>
                {article.isHot && (
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-primary-500 text-background-50">
                    热门
                  </span>
                )}
                {article.tags.slice(0, 2).map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700">
                    #{tag}
                  </span>
                ))}
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-secondary-500">
                <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> {article.date}</span>
                <span className="flex items-center gap-1"><i className="ri-eye-line"></i> {(article.views / 10000).toFixed(1)}万 阅读</span>
                <span className="flex items-center gap-1"><i className="ri-chat-3-line"></i> {article.comments} 评论</span>
              </div>
            </header>

            <div className="mb-10 rounded-xl overflow-hidden">
              <img src={article.image} alt={article.title} className="w-full aspect-[16/9] object-cover" />
            </div>

            <div
              className="prose prose-base md:prose-lg max-w-none text-foreground-800 leading-relaxed
                [&_p]:mb-5 [&_p]:text-foreground-800 [&_p]:leading-loose
                [&_strong]:text-foreground-950 [&_strong]:font-bold"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/60">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1.5 rounded-full text-xs bg-secondary-100 text-secondary-700">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-18 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8">
                相关爆料推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </div>
          </section>
        )}

        {otherHot.length > 0 && (
          <section className="relative w-full py-20 md:py-28 px-4 md:px-6 overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20dramatic%20background%20with%20layered%20geometric%20forms%20in%20deep%20burgundy%20and%20charcoal%2C%20luxurious%20texture%2C%20editorial%20magazine%20style%2C%20dark%20moody%20atmosphere%2C%20warm%20accents%2C%20clean%20minimal%20composition&width=1600&height=600&seq=cta-bg-article&orientation=landscape"
                alt=""
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/55"></div>
            </div>
            <div className="relative z-10 max-w-7xl mx-auto">
              <div className="md:max-w-lg mb-10">
                <h2 className="font-heading text-3xl md:text-5xl font-bold text-white leading-tight mb-3">
                  还没吃够？
                  <br />
                  继续看更多爆料
                </h2>
                <p className="text-white/70 text-sm max-w-xs">
                  更多热门吃瓜内容等你来发现
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {otherHot.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-14 md:py-18 px-4 md:px-6 bg-accent-50/50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <div className="flex items-center justify-center gap-2 mb-4">
                <span className="w-2 h-2 rounded-full bg-accent-500"></span>
                <span className="text-xs font-semibold text-accent-700 uppercase tracking-wider">吃瓜讨论</span>
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                关于这篇爆料的讨论
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 max-w-4xl mx-auto">
              {[
                { title: '不会是炒作吧', body: '这个时间点爆料也太巧了吧，新剧马上要播了，感觉是团队自导自演的炒作。不过瓜还是好吃的，先收藏了。', author: '吃瓜不信瓜', stars: 4 },
                { title: '等一个官方回应', body: '这种事情还是等官方回应吧，现在网上假消息太多了。不过作者写的挺详细的，感觉可信度还可以。', author: '理性吃瓜人', stars: 4 },
                { title: '意料之中的塌房', body: '早就有预感了，果然不出所料。这些所谓的顶流私底下都没几个干净的，只是早晚的问题。', author: '内娱观察员', stars: 5 },
              ].map((review, idx) => (
                <div key={idx} className="bg-background-50 rounded-xl p-5 border border-background-200/60">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }, (_, i) => (
                      <i key={i} className={`text-sm ${i < review.stars ? 'ri-star-fill text-accent-500' : 'ri-star-fill text-secondary-300'}`}></i>
                    ))}
                  </div>
                  <h4 className="text-sm font-bold text-foreground-950 mb-2">{review.title}</h4>
                  <p className="text-xs text-secondary-600 leading-relaxed mb-3 line-clamp-3">{review.body}</p>
                  <p className="text-xs font-semibold text-foreground-950">{review.author}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}